import { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch } from '../../store/types';
import { textKeyLookup, createTranslationSliceSelector, TranslationState } from './ducks';
import { Translation, TypedTranslation } from './types';
import interpolate, { InterpolationValues } from './interpolate';
import { selectIsBotRequest } from '../brand/ducks/selectors';

export type TranslateFunction = (textKey: string, interpolationValues?: InterpolationValues) => string;

/**
 * @typedef {Object} UseHookResult
 * @property {boolean} isLoadingTranslations - Whether translations are being loaded
 * @property {Object<string, string>} translations - Key Value Object where the key is the text key and the value is the translated text
 * @property {Function} translate - Function that translates a text key
 */

/**
 * React Hook that fetches and returns translations of passed textkeys.
 *
 * Translations are fetched from the redux state or from the textKey service
 *
 * @param {Array<String>} [textKeys=[]] - Array of textkeys to translate
 * @returns {UseHookResult}
 *
 * @example
 *
 * // A simple react component
 * function MyComponent() {
 *   const { translations, isLoadingTranslations } = useTranslations(['MY_TEXT_KEY']);
 *
 *   if (isLoadingTranslations) return null;
 *
 *   return (<>{translations.MY_TEXT_KEY}</>);
 * }
 *
 */
const EMPTY_OBJECT = Object.freeze({} as Record<string, string>);
const BOT_TRANSLATION_SUFFIX = '_BOT_REQUEST';

export type TranslationHash = { [key: string]: string };

function getBotTranslations(botKeys: TranslationHash, translationsObj: TranslationHash): TranslationHash {
	const result = {} as { [key: string]: string };
	Object.keys(botKeys).forEach(key => {
		if (translationsObj[botKeys[key]] && translationsObj[botKeys[key]] !== `??${botKeys[key]}??`) {
			result[key] = translationsObj[botKeys[key]];
		} else {
			result[key] = translationsObj[key];
		}
	});
	return result;
}

function getGoogleKeysHash(requestedKeys: string[]): TranslationHash {
	return requestedKeys.reduce((acc, current) => {
		acc[current] = `${current}${BOT_TRANSLATION_SUFFIX}`;
		return acc;
	}, {} as TranslationHash);
}

export default function useTranslations(textKeys: string[] = [], partialResult = false) {
	const dispatch: AppDispatch = useDispatch();
	const isBotRequest = useSelector(selectIsBotRequest);

	const textKeysCombined = isBotRequest
		? textKeys.reduce((key, acc) => {
				key.push(`${acc}${BOT_TRANSLATION_SUFFIX}`);
				key.push(`${acc}`);
				return key;
		  }, [] as string[])
		: textKeys;
	const selectTranslationSlice = useMemo(createTranslationSliceSelector, []);
	const translations = useSelector(state => selectTranslationSlice(state as TranslationState, textKeysCombined));

	const missingTextKeys = useMemo(
		() => textKeysCombined.filter(textKey => typeof translations[textKey] === 'undefined'),
		[textKeysCombined, translations]
	);
	const isMissingTextKeys = missingTextKeys.length > 0;

	useEffect(() => {
		if (isMissingTextKeys) {
			dispatch(textKeyLookup(missingTextKeys));
		}
	}, [dispatch, isMissingTextKeys, missingTextKeys]);

	const translate = useCallback<TranslateFunction>(
		(textKey, interpolationValues) => {
			const text = translations[textKey];
			// No text key found
			if (!text) {
				return '';
			}

			return interpolationValues ? interpolate(text, interpolationValues) : text;
		},
		[translations]
	);

	const finalTranslations = isBotRequest
		? getBotTranslations(getGoogleKeysHash(textKeys), translations)
		: translations;

	if (isMissingTextKeys) {
		return {
			translations: partialResult ? finalTranslations : EMPTY_OBJECT,
			isLoadingTranslations: true,
			translate
		};
	}

	return {
		translations: finalTranslations,
		isLoadingTranslations: isMissingTextKeys,
		translate
	};
}

type UseTypedTranslationResult<T extends Translation> =
	| {
			translations: Readonly<TypedTranslation<T>>;
			isLoadingTranslations: false;
			translate: TranslateFunction;
	  }
	| {
			translations: Readonly<Record<never, never>>;
			isLoadingTranslations: true;
			translate: TranslateFunction;
	  };

export function useTypedTranslations<T extends Translation>(textKeyEnum: T) {
	const textKeys = Object.values(textKeyEnum);
	return useTranslations(textKeys) as UseTypedTranslationResult<T>;
}

export function useTypedTranslationsWithPartialResult<T extends Translation>(textKeyEnum: T) {
	const textKeys = Object.values(textKeyEnum);
	return useTranslations(textKeys, true);
}
